import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useAuth } from "@/lib/auth"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { apiClient, type BillingSubscription } from "@/lib/api"
import { trackEvent } from "@/lib/posthog"
import { Check, CreditCard, ExternalLink, Crown, Sparkles, Home, Lock, ArrowRight, ChevronDown, Download, Shield, AlertTriangle, ListChecks, Plug, Users, Bell, Calendar, type LucideIcon } from "lucide-react"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { useOnboardingChecklist } from "@/lib/useOnboardingChecklist"

// ---- Types ----

interface AlertSubscription {
  id: string
  type: string
  target: string
  enabled: number | boolean
  severity_threshold: string
  created_at: string
  updated_at: string
}

interface AlertEvent {
  id: string
  ts: string
  severity: string
  event_type: string
  summary: string
  delivery_status: string
  delivered_at: string
}

interface Member {
  user_id: string
  email: string
  name: string
  role: string
  is_owner: boolean
  created_at: string
}

// ---- Settings Page ----

type SettingsTabId = "setup" | "integrations" | "general" | "alerts" | "members" | "billing" | "privacy"

export function SettingsPage() {
  const { propertyId } = usePropertyContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const billingParam = searchParams.get("billing")
  const tabParam = searchParams.get("tab")
  const urlTabs = ["general", "alerts", "members", "billing", "privacy"] as const
  const initialTab: SettingsTabId = billingParam ? "billing"
    : (urlTabs as readonly string[]).includes(tabParam || "") ? (tabParam as SettingsTabId)
    : "setup"
  const [tab, setTab] = useState<SettingsTabId>(initialTab)
  const onboarding = useOnboardingChecklist(propertyId)
  const setupIncomplete = Math.max(0, onboarding.totalCount - onboarding.completedCount)

  const sections: { key: SettingsTabId; label: string; icon: LucideIcon; badge?: number }[] = [
    { key: "setup", label: "Setup", icon: ListChecks, badge: onboarding.isLoading ? 0 : setupIncomplete },
    { key: "integrations", label: "Integrations", icon: Plug },
    { key: "general", label: "Property", icon: Home },
    { key: "billing", label: "Plan & billing", icon: CreditCard },
    { key: "members", label: "Team", icon: Users },
    { key: "alerts", label: "Notifications", icon: Bell },
    { key: "privacy", label: "Privacy", icon: Shield },
  ]

  return (
    <div className="mx-auto max-w-[1060px] px-4 pb-16 pt-6 md:px-7">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr] md:items-start">
        <nav className="flex gap-1 overflow-x-auto pb-1 md:sticky md:top-5 md:flex-col md:gap-0.5 md:overflow-visible md:pb-0">
          {sections.map((s) => {
            const active = tab === s.key
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setTab(s.key)}
                className={cn(
                  "flex items-center gap-2.5 whitespace-nowrap rounded-[9px] px-[11px] py-2.5 text-left text-[13px] font-semibold transition-colors",
                  active ? "bg-brand-soft text-brand-strong" : "text-ink-2 hover:bg-line-soft",
                )}
              >
                <s.icon className="h-4 w-4 flex-none" strokeWidth={1.7} />
                <span className="flex-1">{s.label}</span>
                {s.badge ? (
                  <span className="rounded-[6px] bg-amber-soft px-1.5 py-px font-mono text-[10px] font-semibold text-amber-strong">{s.badge}</span>
                ) : null}
              </button>
            )
          })}
        </nav>

        <div className="min-w-0">
          {tab === "setup" && <SetupPanel onboarding={onboarding} propertyId={propertyId} onGoIntegrations={() => setTab("integrations")} />}
          {tab === "integrations" && <IntegrationsPanel propertyId={propertyId} onManage={setTab} />}
          {tab === "general" && <GeneralSettings />}
          {tab === "alerts" && <AlertsSettings />}
          {tab === "members" && <MembersSettings />}
          {tab === "billing" && <BillingSettings billingParam={billingParam} clearBillingParam={() => {
            searchParams.delete("billing")
            setSearchParams(searchParams, { replace: true })
          }} />}
          {tab === "privacy" && <PrivacySettings />}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Setup Panel — onboarding ring + checklist (real activation-driven completion)
// ============================================

function SetupPanel({
  onboarding,
  propertyId,
  onGoIntegrations,
}: {
  onboarding: ReturnType<typeof useOnboardingChecklist>
  propertyId: string
  onGoIntegrations: () => void
}) {
  const navigate = useNavigate()
  const { items, completedCount, totalCount, isLoading } = onboarding
  const pct = totalCount ? Math.round((completedCount / totalCount) * 100) : 0
  const circ = 2 * Math.PI * 32
  const go = (linkTo: string) => {
    // Items with no section (dashboard-level steps) route to the property home.
    if (!linkTo) navigate(`/properties/${propertyId}`)
    else if (linkTo.startsWith("/")) navigate(linkTo)
    else navigate(`/properties/${propertyId}/${linkTo}`)
  }

  return (
    <div>
      <section
        className="relative mb-4 overflow-hidden rounded-card px-6 py-[22px] text-[#EAF3EE] shadow-sm"
        style={{ background: "radial-gradient(130% 150% at 100% 0%,#1c4c3b 0%,var(--sf-forest) 60%)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#7fb6a0]">Getting set up</div>
            <h2 className="mt-1.5 font-display text-[21px] font-bold tracking-[-0.02em]">You’re {isLoading ? "…" : `${pct}%`} of the way to fully live</h2>
            <p className="mt-1.5 max-w-[48ch] text-[13px] leading-[1.5] text-[#B9D0C6]">Finish these to unlock the full boutique-hotel experience.</p>
          </div>
          <div className="relative h-[78px] w-[78px] flex-none">
            <svg width="78" height="78" viewBox="0 0 78 78">
              <circle cx="39" cy="39" r="32" fill="none" stroke="rgba(255,255,255,.16)" strokeWidth="7" />
              <circle cx="39" cy="39" r="32" fill="none" stroke="#3ee0a0" strokeWidth="7" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - (totalCount ? completedCount / totalCount : 0))} transform="rotate(-90 39 39)" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-display text-[18px] font-bold">
              {isLoading ? "—" : `${completedCount}/${totalCount}`}
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-card border border-border bg-card px-5 py-1 shadow-sm">
        {isLoading ? (
          <div className="space-y-2 py-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-line-soft" />)}</div>
        ) : (
          items.map((item) => (
            <div key={item.key} className="flex items-center gap-3.5 border-t border-line-soft py-[15px] first:border-t-0">
              <span
                className={cn(
                  "flex h-6 w-6 flex-none items-center justify-center rounded-full border-2",
                  item.completed ? "border-brand bg-brand" : "border-line-2 bg-card",
                )}
              >
                {item.completed && <Check className="h-3 w-3 text-white" strokeWidth={2.8} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className={cn("text-[14px] font-semibold", item.completed ? "text-muted-foreground line-through" : "text-foreground")}>{item.label}</div>
              </div>
              {!item.completed && (
                <button
                  type="button"
                  onClick={() => (item.key === "property_connected" ? onGoIntegrations() : go(item.linkTo))}
                  className="inline-flex flex-none items-center gap-1.5 rounded-[9px] bg-forest px-3.5 py-2 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                  Set up
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ============================================
// Integrations Panel — overview cards (channels/iCal → Property, Stripe → Billing)
// ============================================

function IntegrationsPanel({ propertyId, onManage }: { propertyId: string; onManage: (tab: SettingsTabId) => void }) {
  const [pmsConnected, setPmsConnected] = useState(false)
  const [pmsProvider, setPmsProvider] = useState("")
  const [icalConnected, setIcalConnected] = useState(false)

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    Promise.allSettled([apiClient.getPropertySettings(propertyId), apiClient.getCalendarFeeds(propertyId)]).then(([settingsRes, feedsRes]) => {
      if (cancelled) return
      if (settingsRes.status === "fulfilled") {
        const integration = (settingsRes.value as { settings?: { integration?: Record<string, unknown> } }).settings?.integration
        setPmsConnected(Boolean(integration?.has_public_api_key || integration?.pms_property_ref))
        const provider = String(integration?.pms_provider || "").trim()
        setPmsProvider(provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : "")
      }
      if (feedsRes.status === "fulfilled") {
        const feeds = (feedsRes.value as { feeds?: unknown[] }).feeds
        setIcalConnected(Array.isArray(feeds) && feeds.length > 0)
      }
    })
    return () => {
      cancelled = true
    }
  }, [propertyId])

  // Truthful status: one card per actual integration — no fabricated per-channel claims.
  const cards: {
    key: string; name: string; desc: string; mark: string; icon?: LucideIcon; logoBg: string; logoColor: string
    connected: boolean; comingSoon?: boolean; onClick?: () => void; actionLabel: string
  }[] = [
    { key: "pms", name: pmsProvider || "Booking channel (PMS)", desc: "Listings, bookings & availability sync", mark: pmsProvider ? pmsProvider.charAt(0) : "", icon: pmsProvider ? undefined : Home, logoBg: "bg-brand-soft", logoColor: "text-brand-strong", connected: pmsConnected, onClick: () => onManage("general"), actionLabel: pmsConnected ? "Connected" : "Connect" },
    { key: "ical", name: "iCal sync", desc: "Calendar feeds from Airbnb, Booking.com & more", mark: "", icon: Calendar, logoBg: "bg-line-soft", logoColor: "text-ink-2", connected: icalConnected, onClick: () => onManage("general"), actionLabel: icalConnected ? "Connected" : "Connect" },
    { key: "stripe", name: "Stripe", desc: "Subscription & billing", mark: "S", logoBg: "bg-[#EDE9FE]", logoColor: "text-[#635BFF]", connected: false, onClick: () => onManage("billing"), actionLabel: "Manage" },
    { key: "google", name: "Google Business", desc: "Import & reply to Google reviews", mark: "G", logoBg: "bg-sky-soft", logoColor: "text-sky", connected: false, comingSoon: true, actionLabel: "Coming soon" },
    { key: "lock", name: "Smart lock", desc: "Auto-issue door codes per stay", mark: "", icon: Lock, logoBg: "bg-amber-soft", logoColor: "text-amber-strong", connected: false, comingSoon: true, actionLabel: "Coming soon" },
  ]

  return (
    <div>
      <div className="mb-3.5">
        <h2 className="font-display text-[18px] font-bold tracking-[-0.02em] text-foreground">Integrations</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">Connect the channels and tools that feed your workspace.</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {cards.map((c) => (
          <article key={c.key} className={cn("flex items-center gap-3 rounded-[14px] border bg-card p-4 shadow-sm", c.connected ? "border-brand/40" : "border-border")}>
            <span className={cn("flex h-10 w-10 flex-none items-center justify-center rounded-[11px] font-display text-[15px] font-bold", c.logoBg, c.logoColor)}>
              {c.icon ? <c.icon className="h-[18px] w-[18px]" strokeWidth={1.8} /> : c.mark}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{c.name}</div>
              <div className="truncate text-[11.5px] text-muted-foreground">{c.desc}</div>
            </div>
            <button
              type="button"
              onClick={c.onClick}
              disabled={c.comingSoon}
              className={cn(
                "inline-flex flex-none items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-[12px] font-semibold transition-opacity",
                c.comingSoon
                  ? "cursor-default border border-border bg-card text-muted-foreground"
                  : c.connected
                    ? "bg-brand-soft text-brand-strong hover:opacity-90"
                    : "bg-forest text-white hover:opacity-90",
              )}
            >
              {c.connected && <Check className="h-3 w-3" strokeWidth={2.4} />}
              {c.actionLabel}
            </button>
          </article>
        ))}
      </div>
    </div>
  )
}

// ============================================
// General Settings Tab
// ============================================

interface WeatherSnapshot {
  available: boolean
  source?: string
  timezone?: string
  current?: {
    temperature_c: number
    weather_code: number
    weather_label: string
    wind_speed_ms: number | null
    wind_gusts_ms: number | null
    humidity_pct: number | null
    cloud_cover_pct: number | null
    is_day: boolean
  }
  daily?: {
    sunrise: string | null
    sunset: string | null
    temp_max_c: number | null
    temp_min_c: number | null
  }
}

function GeneralSettings() {
  const { propertyId } = usePropertyContext()
  const [settings, setSettings] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  // PMS API key is write-only. Backend only returns a masked value.
  const [pmsApiKey, setPmsApiKey] = useState("")
  const [pmsKeyDirty, setPmsKeyDirty] = useState(false)
  // Guesty dual-credential fields
  const [guestyClientId, setGuestyClientId] = useState("")
  const [guestyClientSecret, setGuestyClientSecret] = useState("")
  const [guestyKeyDirty, setGuestyKeyDirty] = useState(false)
  const [doorCodeDirty, setDoorCodeDirty] = useState(false)
  const [doorCodeValue, setDoorCodeValue] = useState("")
  const [testingConnection, setTestingConnection] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [weatherSnapshot, setWeatherSnapshot] = useState<WeatherSnapshot | null>(null)
  const [weatherLoading, setWeatherLoading] = useState(false)

  const fetchWeather = useCallback(async (s?: Record<string, unknown>) => {
    if (!propertyId) return
    const src = s || settings
    const integration = (src.integration as Record<string, unknown> | undefined) || {}
    const lat = Number(integration.lat) || undefined
    const lng = Number(integration.lng) || undefined
    const tz = String(integration.timezone || "") || undefined
    if (!lat || !lng) return
    setWeatherLoading(true)
    try {
      const res = await apiClient.getWeatherCurrent(propertyId, lat, lng, tz)
      if (res.ok && res.weather) setWeatherSnapshot(res.weather as WeatherSnapshot)
    } catch { /* weather fetch is best-effort */ }
    setWeatherLoading(false)
  }, [propertyId, settings])

  useEffect(() => {
    if (!propertyId) return
    setLoading(true)
    apiClient.getPropertySettings(propertyId).then((res) => {
      if (res.ok && res.settings) {
        setSettings(res.settings)
        fetchWeather(res.settings)
      }
      setLoading(false)
    })
  }, [propertyId]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(key: string, value: string) {
    setSettings((s) => {
      if (key.includes(".")) {
        const [parent, child] = key.split(".")
        const parentObj = (s[parent] as Record<string, unknown>) || {}
        return { ...s, [parent]: { ...parentObj, [child]: value } }
      }
      return { ...s, [key]: value }
    })
    setDirty(true)
    setSuccess("")
  }

  function getNestedValue(key: string): string {
    if (key.includes(".")) {
      const [parent, child] = key.split(".")
      const parentObj = settings[parent] as Record<string, unknown> | undefined
      return String(parentObj?.[child] ?? "")
    }
    return String(settings[key] ?? "")
  }

  function handlePmsApiKeyChange(value: string) {
    setPmsApiKey(value)
    setPmsKeyDirty(true)
    setDirty(true)
    setSuccess("")
  }

  async function handleTestConnection() {
    if (!propertyId) return

    setTestingConnection(true)
    setTestResult(null)
    try {
      const provider = getNestedValue("integration.pms_provider")
      let key = pmsKeyDirty ? pmsApiKey.trim() : ""
      // Guesty/Hostaway/OwnerRez dual-credential: serialize as JSON for test connection
      if (guestyKeyDirty && guestyClientId.trim() && guestyClientSecret.trim()) {
        if (provider === "guesty") {
          key = JSON.stringify({ clientId: guestyClientId.trim(), clientSecret: guestyClientSecret.trim() })
        } else if (provider === "hostaway") {
          key = JSON.stringify({ accountId: guestyClientId.trim(), apiKey: guestyClientSecret.trim() })
        } else if (provider === "ownerrez") {
          key = JSON.stringify({ email: guestyClientId.trim(), token: guestyClientSecret.trim() })
        }
      }
      const res = key
        ? await apiClient.fetchPmsUnits(key, provider)
        : await apiClient.syncPmsUnits(propertyId)
      if (res.ok && res.units) {
        setTestResult({
          success: true,
          message: `Connected — ${res.units.length} propert${res.units.length === 1 ? "y" : "ies"} found`,
        })
      } else {
        setTestResult({
          success: false,
          message: res.error || "Connection failed. Check your API key.",
        })
      }
    } catch {
      setTestResult({ success: false, message: "Network error. Please try again." })
    }
    setTestingConnection(false)
  }

  async function handleSave() {
    if (!propertyId) return

    setSaving(true)
    setError("")
    setSuccess("")
    const payload: Record<string, unknown> = { ...settings }
    // Flatten door_code nested object to flat keys the backend expects
    if (typeof payload.door_code === "object" && payload.door_code !== null) {
      const dc = payload.door_code as Record<string, unknown>
      payload.door_code = dc.door_code ?? ""
      payload.checkin_time = dc.checkin_time ?? "15:00"
      payload.door_code_offset_minutes = dc.offset_minutes ?? 0
      payload.door_code_show_guidebook = dc.show_guidebook
      payload.door_code_show_display = dc.show_display
    }
    // Flatten guidebook_template from property_config for backend
    const pc = (payload.property_config as Record<string, unknown> | undefined) ?? {}
    if (typeof pc.guidebook_template === "string") {
      payload.guidebook_template = pc.guidebook_template
    }
    if (typeof pc.house_voice === "string") {
      payload.house_voice = pc.house_voice
    }
    if (pmsKeyDirty && pmsApiKey.trim()) {
      payload.public_api_key = pmsApiKey.trim()
    }
    // Guesty/Hostaway/OwnerRez dual-credential: serialize as JSON before sending
    const currentProvider = getNestedValue("integration.pms_provider")
    if (guestyKeyDirty && guestyClientId.trim() && guestyClientSecret.trim()) {
      if (currentProvider === "guesty") {
        payload.public_api_key = JSON.stringify({ clientId: guestyClientId.trim(), clientSecret: guestyClientSecret.trim() })
      } else if (currentProvider === "hostaway") {
        payload.public_api_key = JSON.stringify({ accountId: guestyClientId.trim(), apiKey: guestyClientSecret.trim() })
      } else if (currentProvider === "ownerrez") {
        payload.public_api_key = JSON.stringify({ email: guestyClientId.trim(), token: guestyClientSecret.trim() })
      }
    }
    const res = await apiClient.savePropertySettings(payload, propertyId)
    if (res.ok) {
      setDirty(false)
      setPmsApiKey("")
      setPmsKeyDirty(false)
      setGuestyClientId("")
      setGuestyClientSecret("")
      setGuestyKeyDirty(false)
      setDoorCodeDirty(false)
      setDoorCodeValue("")
      setTestResult(null)
      setSuccess("Settings saved")
      if (res.warning) setSuccess(`Settings saved. ${res.warning}`)
      if (res.settings) {
        setSettings(res.settings)
        fetchWeather(res.settings)
      }
    } else {
      setError(res.error || "Failed to save")
    }
    setSaving(false)
  }

  const integration = (settings.integration as Record<string, unknown> | undefined) || {}
  const hasStoredPmsKey = Boolean(integration.has_public_api_key)
  const hasPendingPmsKey = Boolean(pmsKeyDirty && pmsApiKey.trim()) || Boolean(guestyKeyDirty && guestyClientId.trim() && guestyClientSecret.trim())
  const hasPmsConfigured = hasStoredPmsKey || hasPendingPmsKey
  const selectedPmsProvider = getNestedValue("integration.pms_provider")

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="sf-spinner" />
      </div>
    )
  }

  return (
    <>
      {error && <div className="login-card__error mb-4" role="alert">{error}</div>}
      {success && (
        <div className="settings-success-banner">{success}</div>
      )}

      <div className="section-card" id="booking-integration">
        <h2 className="section-card__title">Booking Integration</h2>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className={`status-pill status-pill--${hasPmsConfigured ? "healthy" : "neutral"}`}>
            <span className="status-pill__dot" />
            PMS {hasPmsConfigured ? "configured" : "not configured"}
          </span>
        </div>
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          {hasPmsConfigured
            ? "Bookings are synced via PMS API."
            : "Configure PMS API credentials here. iCal feeds are managed on the Calendar page."}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsField
            label="PMS Provider"
            value={getNestedValue("integration.pms_provider")}
            onChange={(v) => handleChange("integration.pms_provider", v)}
            type="select"
            hint="Only needed if this property syncs bookings via PMS API."
            options={[
              { value: "lodgify", label: "Lodgify" },
              { value: "hostfully", label: "Hostfully" },
              { value: "guesty", label: "Guesty" },
              { value: "hostaway", label: "Hostaway" },
              { value: "ownerrez", label: "OwnerRez" },
              { value: "smoobu", label: "Smoobu" },
              { value: "other", label: "Other" },
            ]}
          />
          {selectedPmsProvider === "guesty" || selectedPmsProvider === "hostaway" || selectedPmsProvider === "ownerrez" ? (
            <div>
              <label className="login-card__label" htmlFor="settings-guesty-client-id">
                {selectedPmsProvider === "ownerrez" ? "OwnerRez Credentials" : selectedPmsProvider === "hostaway" ? "Hostaway Credentials" : "Guesty Credentials"}
              </label>
              {hasStoredPmsKey && !guestyKeyDirty && (
                <div className="settings-credential-row">
                  <span className="status-pill status-pill--healthy text-xs">
                    <span className="status-pill__dot" />
                    Configured
                  </span>
                </div>
              )}
              <input
                id="settings-guesty-client-id"
                className="login-card__input h-9 text-sm mb-2"
                type={selectedPmsProvider === "ownerrez" ? "email" : "text"}
                value={guestyClientId}
                onChange={(e) => { setGuestyClientId(e.target.value); setGuestyKeyDirty(true); setDirty(true); setSuccess("") }}
                placeholder={hasStoredPmsKey
                  ? (selectedPmsProvider === "ownerrez" ? "Enter new email to update..." : selectedPmsProvider === "hostaway" ? "Enter new Account ID to update..." : "Enter new Client ID to update...")
                  : (selectedPmsProvider === "ownerrez" ? "you@example.com" : selectedPmsProvider === "hostaway" ? "Hostaway Account ID" : "Guesty OAuth2 Client ID")}
              />
              <input
                id="settings-guesty-client-secret"
                className="login-card__input h-9 text-sm"
                type="password"
                value={guestyClientSecret}
                onChange={(e) => { setGuestyClientSecret(e.target.value); setGuestyKeyDirty(true); setDirty(true); setSuccess("") }}
                placeholder={hasStoredPmsKey
                  ? (selectedPmsProvider === "ownerrez" ? "Enter new token to update..." : selectedPmsProvider === "hostaway" ? "Enter new API Key to update..." : "Enter new Client Secret to update...")
                  : (selectedPmsProvider === "ownerrez" ? "pt_..." : selectedPmsProvider === "hostaway" ? "Hostaway API Key" : "Guesty OAuth2 Client Secret")}
              />
              <div className="settings-field-hint">
                {hasStoredPmsKey
                  ? "Leave empty to keep current credentials"
                  : "Credentials are encrypted before storage"}
              </div>
            </div>
          ) : (
            <div>
              <label className="login-card__label" htmlFor="settings-pms-api-key">
                PMS API Key
              </label>
              {hasStoredPmsKey && !pmsKeyDirty && (
                <div className="settings-credential-row">
                  <span className="status-pill status-pill--healthy text-xs">
                    <span className="status-pill__dot" />
                    Configured
                  </span>
                  <span className="text-muted-foreground font-mono text-xs">
                    {String(integration.public_api_key_masked || "")}
                  </span>
                </div>
              )}
              <input
                id="settings-pms-api-key"
                className="login-card__input h-9 text-sm"
                type="password"
                value={pmsApiKey}
                onChange={(e) => handlePmsApiKeyChange(e.target.value)}
                placeholder={
                  hasStoredPmsKey
                    ? "Enter new key to update..."
                    : "Enter API key (optional if using only iCal)..."
                }
              />
              <div className="settings-field-hint">
                {hasStoredPmsKey
                  ? "Leave empty to keep current key"
                  : "Your API key is encrypted before storage"}
              </div>
            </div>
          )}
        </div>

        {(hasPmsConfigured || pmsApiKey.trim()) && (
          <div className="mt-3">
            <button
              className="sf-btn sf-btn--secondary sf-btn--sm"
              onClick={handleTestConnection}
              disabled={testingConnection}
              type="button"
            >
              {testingConnection ? "Testing..." : "Test PMS Connection"}
            </button>
          </div>
        )}

        {testResult && (
          <div className={`sf-result-banner sf-result-banner--${testResult.success ? "success" : "error"} mt-2`}>
            {testResult.message}
          </div>
        )}
      </div>

      {/* Property Details */}
      <div className="section-card">
        <h2 className="section-card__title">Property Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AddressAutocomplete
            value={getNestedValue("integration.address")}
            onChange={(v) => handleChange("integration.address", v)}
            onPlaceSelect={(details) => {
              handleChange("integration.address", details.address)
              if (details.lat != null) handleChange("integration.lat", String(details.lat))
              if (details.lng != null) handleChange("integration.lng", String(details.lng))
              const currentTimezone = getNestedValue("integration.timezone")
              if (details.timezone) {
                handleChange("integration.timezone", details.timezone)
              } else if (!currentTimezone || currentTimezone === "UTC") {
                const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
                if (browserTz) handleChange("integration.timezone", browserTz)
              }
            }}
          />
          <TimezoneSelect
            value={getNestedValue("integration.timezone")}
            onChange={(v) => handleChange("integration.timezone", v)}
          />
          <SettingsField
            label="Latitude"
            value={getNestedValue("integration.lat")}
            onChange={(v) => handleChange("integration.lat", v)}
            hint="Auto-filled from address, or enter manually"
          />
          <SettingsField
            label="Longitude"
            value={getNestedValue("integration.lng")}
            onChange={(v) => handleChange("integration.lng", v)}
            hint="Auto-filled from address, or enter manually"
          />
          <SettingsField
            label="WiFi Name"
            value={getNestedValue("wifi_name")}
            onChange={(v) => handleChange("wifi_name", v)}
          />
          <SettingsField
            label="WiFi Password"
            value={getNestedValue("wifi_password")}
            onChange={(v) => handleChange("wifi_password", v)}
          />
        </div>
      </div>

      {/* Weather Confirmation Card */}
      {weatherSnapshot && weatherSnapshot.available && weatherSnapshot.current && (
        <div className="section-card">
          <h2 className="section-card__title">Weather Confirmation</h2>
          <p className="m-0 mb-3 text-sm text-muted-foreground">
            Live weather data from Open-Meteo for your property coordinates. This data is used for display rendering and AI message generation.
          </p>
          <div className="flex gap-6 flex-wrap items-start">
            <div>
              <div className="text-[2.5rem] font-semibold leading-none">
                {Math.round(weatherSnapshot.current.temperature_c)}°C
              </div>
              <div className="text-sm text-foreground/70 mt-1">
                {weatherSnapshot.current.weather_label}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              {weatherSnapshot.current.wind_speed_ms != null && (
                <div>
                  <span className="text-muted-foreground">Wind</span>
                  <div>{Math.round(weatherSnapshot.current.wind_speed_ms)} m/s
                    {weatherSnapshot.current.wind_gusts_ms != null && (
                      <span className="text-muted-foreground"> (gusts {Math.round(weatherSnapshot.current.wind_gusts_ms)})</span>
                    )}
                  </div>
                </div>
              )}
              {weatherSnapshot.current.humidity_pct != null && (
                <div>
                  <span className="text-muted-foreground">Humidity</span>
                  <div>{weatherSnapshot.current.humidity_pct}%</div>
                </div>
              )}
              {weatherSnapshot.daily?.sunset && (
                <div>
                  <span className="text-muted-foreground">Sunset</span>
                  <div>{formatClock(weatherSnapshot.daily.sunset, weatherSnapshot.timezone)}</div>
                </div>
              )}
              {weatherSnapshot.daily?.sunrise && (
                <div>
                  <span className="text-muted-foreground">Sunrise</span>
                  <div>{formatClock(weatherSnapshot.daily.sunrise, weatherSnapshot.timezone)}</div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Source: {weatherSnapshot.source || "open-meteo"} | Timezone: {weatherSnapshot.timezone || "auto"}
          </div>
        </div>
      )}
      {weatherLoading && !weatherSnapshot && (
        <div className="section-card text-center p-6">
          <div className="sf-spinner" />
          <p className="mt-2 text-sm text-muted-foreground">Loading weather data...</p>
        </div>
      )}

      {/* Access Settings */}
      <div className="section-card">
        <h2 className="section-card__title">Access</h2>
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          Door code shown to guests during their stay. Hidden before check-in time and on checkout day.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="login-card__label">Door Code</label>
            {getNestedValue("door_code.door_code") && !doorCodeDirty && (
              <div className="settings-credential-row">
                <span className="status-pill status-pill--healthy text-xs">
                  <span className="status-pill__dot" />
                  Saved
                </span>
              </div>
            )}
            <input
              className="login-card__input h-9 text-sm"
              type="text"
              value={doorCodeDirty ? doorCodeValue : ""}
              onChange={(e) => {
                setDoorCodeValue(e.target.value)
                setDoorCodeDirty(true)
                handleChange("door_code.door_code", e.target.value)
              }}
              placeholder={getNestedValue("door_code.door_code") ? "Enter new code to update..." : "4-8 digit code"}
            />
            <div className="settings-field-hint">
              Leave empty to disable door code in the guidebook and display.
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="login-card__label">Early access offset</label>
          <p className="m-0 mb-2 text-xs text-muted-foreground">
            How early before check-in time should the door code become visible?
          </p>
          <div className="sf-segment">
            {[
              { value: "0", label: "At check-in time" },
              { value: "30", label: "30 min before" },
              { value: "60", label: "1 hour before" },
              { value: "120", label: "2 hours before" },
            ].map((opt) => {
              const currentOffset = String(getNestedValue("door_code.offset_minutes") || "0")
              const isActive = currentOffset === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`sf-segment__btn${isActive ? " sf-segment__btn--active" : ""}`}
                  onClick={() => { handleChange("door_code.offset_minutes", opt.value) }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-4">
          <label className="login-card__label">Show door code on</label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={getNestedValue("door_code.show_guidebook") !== "0" && getNestedValue("door_code.show_guidebook") !== "false"}
                onChange={(e) => handleChange("door_code.show_guidebook", e.target.checked ? "1" : "0")}
              />
              Web guidebook
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={getNestedValue("door_code.show_display") !== "0" && getNestedValue("door_code.show_display") !== "false"}
                onChange={(e) => handleChange("door_code.show_display", e.target.checked ? "1" : "0")}
              />
              TRMNL display
            </label>
          </div>
        </div>
      </div>


      {/* Guidebook Template */}
      <div className="section-card">
        <h2 className="section-card__title">Guidebook Template</h2>
        <p className="m-0 mb-3 text-sm text-muted-foreground">
          Choose the visual style for your guest-facing guidebook.
        </p>
        <div className="sf-segment">
          {[
            { value: "classic", label: "Classic" },
            { value: "concierge", label: "Concierge" },
            { value: "reserve", label: "Reserve" },
          ].map((opt) => {
            const current = getNestedValue("property_config.guidebook_template") || "classic"
            return (
              <button
                key={opt.value}
                type="button"
                className={`sf-segment__btn${current === opt.value ? " sf-segment__btn--active" : ""}`}
                onClick={() => handleChange("property_config.guidebook_template", opt.value)}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
        <div className="settings-field-hint">
          Classic: warm, editorial scroll layout. Concierge: minimal, tab-based mobile design. Reserve: premium hotel-style mobile concierge with glass cards and editorial typography.
        </div>
      </div>

      {/* House Voice */}
      <HouseVoiceSelector getNestedValue={getNestedValue} handleChange={handleChange} />

      {/* Save */}
      {dirty && (
        <div className="unsaved-bar">
          <span>You have unsaved changes</span>
          <div className="unsaved-bar__actions">
            <button
              className="data-table__action h-9 text-white border-white/30"
              onClick={() => window.location.reload()}
              type="button"
            >
              Discard
            </button>
            <button
              className="login-card__submit w-auto px-5 h-9"
              onClick={handleSave}
              disabled={saving}
              type="button"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <DangerZone />
    </>
  )
}

// ============================================
// House Voice Selector
// ============================================

const VOICE_OPTIONS = [
  {
    value: "house_standard",
    label: "House Standard",
    tier: "host" as const,
    isDefault: true,
    description: "Calm, premium, polished. The default Stayframe voice.",
    sample: "Another relaxing day is ready for you.",
  },
  {
    value: "attentive_host",
    label: "Attentive Host",
    tier: "insight" as const,
    isDefault: false,
    description: "Warm, proactive, service-forward. Guests feel looked after.",
    sample: "We\u2019re so glad you\u2019re here.",
  },
  {
    value: "english_butler",
    label: "English Butler",
    tier: "concierge" as const,
    isDefault: false,
    description: "Formal, discreet. British English, quietly attentive.",
    sample: "All has been prepared for your comfort.",
  },
  {
    value: "warm_local",
    label: "Warm Local",
    tier: "concierge" as const,
    isDefault: false,
    description: "Personal, first-person, conversational warmth.",
    sample: "So glad you\u2019re here! Make yourself at home.",
  },
  {
    value: "minimalist",
    label: "Minimalist",
    tier: "insight" as const,
    isDefault: false,
    description: "Short. Direct. Zero fluff.",
    sample: "You\u2019re here. Relax.",
  },
]

const TIER_RANK: Record<string, number> = { host: 0, insight: 1, concierge: 2 }

// Subset of voices shown in the Host upsell preview (4 representative samples)
const HOST_PREVIEW_VOICES = VOICE_OPTIONS.filter(
  (v) => ["house_standard", "attentive_host", "english_butler", "minimalist"].includes(v.value),
)

function VoiceCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary shrink-0">
      <polyline points="3 8.5 6.5 12 13 4" />
    </svg>
  )
}

function VoiceLockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-muted-foreground shrink-0">
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
    </svg>
  )
}

function HouseVoiceSelector({
  getNestedValue,
  handleChange,
}: {
  getNestedValue: (path: string) => unknown
  handleChange: (path: string, value: string) => void
}) {
  const { plan, canAccess } = usePlanContext()
  const navigate = useNavigate()
  const canUseBasicVoices = canAccess("guest.house_voice")
  const canUseAllVoices = canAccess("guest.house_voice_full")
  const [expandedLocked, setExpandedLocked] = useState<string | null>(null)

  const planRank = TIER_RANK[plan] ?? 0

  // Host plan: voice preview grid + upgrade wall
  if (!canUseBasicVoices) {
    return (
      <div className="section-card">
        <h2 className="section-card__title">House Voice</h2>

        {/* Voice preview grid — let them "hear" each voice */}
        <div className="sf-voice-preview-grid">
          {HOST_PREVIEW_VOICES.map((v) => (
            <div className="sf-voice-preview-card" key={v.value}>
              <div className="sf-voice-preview-card__quote">&ldquo;{v.sample}&rdquo;</div>
              <div className="sf-voice-preview-card__label">&mdash; {v.label}</div>
            </div>
          ))}
        </div>

        {/* Upgrade wall */}
        <div className="sf-voice-upgrade-wall">
          <div className="sf-voice-upgrade-wall__icon">{"\u2726"}</div>
          <h3 className="sf-voice-upgrade-wall__title">Choose Your House Voice</h3>
          <p className="sf-voice-upgrade-wall__desc">
            Set the tone for every guest touchpoint &mdash; from welcome messages to TV displays.
          </p>
          <div className="sf-voice-upgrade-wall__features">
            <div className="sf-voice-upgrade-wall__feature">
              <span className="sf-voice-upgrade-wall__feature-icon">{"\u25C8"}</span>
              <span>5 distinct voices</span>
            </div>
            <div className="sf-voice-upgrade-wall__feature">
              <span className="sf-voice-upgrade-wall__feature-icon">{"\u25C8"}</span>
              <span>AI-tuned copy</span>
            </div>
            <div className="sf-voice-upgrade-wall__feature">
              <span className="sf-voice-upgrade-wall__feature-icon">{"\u25C8"}</span>
              <span>Guidebook headings</span>
            </div>
            <div className="sf-voice-upgrade-wall__feature">
              <span className="sf-voice-upgrade-wall__feature-icon">{"\u25C8"}</span>
              <span>TV display text</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("settings?tab=billing")}
            className="sf-btn sf-btn--primary sf-voice-upgrade-wall__cta"
          >
            Upgrade to Insight
          </button>
        </div>
      </div>
    )
  }

  const currentVoice = (getNestedValue("property_config.house_voice") as string) || "house_standard"

  return (
    <div className="section-card">
      <h2 className="section-card__title">House Voice</h2>
      <p className="sf-voice-subtitle">
        Set the tone for how your property speaks to guests.
      </p>

      <div className="sf-voice-grid">
        {VOICE_OPTIONS.map((opt) => {
          const optRank = TIER_RANK[opt.tier] ?? 0
          const isLocked = optRank > planRank
          const isActive = !isLocked && currentVoice === opt.value
          const isExpanded = expandedLocked === opt.value

          return (
            <button
              key={opt.value}
              type="button"
              className={`sf-voice-card${isActive ? " sf-voice-card--active" : ""}${isLocked ? " sf-voice-card--locked" : ""}`}
              onClick={() => {
                if (isLocked) {
                  setExpandedLocked(isExpanded ? null : opt.value)
                } else {
                  handleChange("property_config.house_voice", opt.value)
                }
              }}
            >
              <div className="sf-voice-card__header">
                <span className="sf-voice-card__radio">
                  {isLocked ? <VoiceLockIcon /> : isActive ? "\u25CF" : "\u25CB"}
                </span>
                <span className="sf-voice-card__name">{opt.label}</span>
                {opt.isDefault && !isActive && <span className="sf-voice-card__default">Default</span>}
                {isActive && <VoiceCheckIcon />}
              </div>
              <div className="sf-voice-card__desc">{opt.description}</div>
              <div className="sf-voice-card__sample">&ldquo;{opt.sample}&rdquo;</div>
              {isLocked && isExpanded && (
                <div className="sf-voice-card__upgrade">
                  Available on Concierge.{" "}
                  <button type="button" className="underline font-medium" onClick={() => navigate("settings?tab=billing")}>Upgrade</button>
                </div>
              )}
            </button>
          )
        })}
      </div>

      <div className="sf-voice-footnote">
        Affects AI messages, guidebook copy, and TV display text. Safety and emergency content always stays clear.
      </div>
    </div>
  )
}

// ============================================
// Billing Settings Tab
// ============================================

const PLAN_INFO: Record<string, { name: string; icon: typeof Home; description: string; features: string[] }> = {
  host: {
    name: "Host",
    icon: Home,
    description: "Your property data, organised.",
    features: [
      "Dashboard with KPIs, heatmaps, and pacing",
      "Template-based guidebook, e-ink display, and TV",
      "Deterministic reputation score",
      "Calendar sync and availability",
      "Audit log and job monitoring",
    ],
  },
  insight: {
    name: "Insight",
    icon: Sparkles,
    description: "AI reads your data and tells you what matters.",
    features: [
      "Everything in Host",
      "AI performance narrative and weekly actions",
      "AI ranked drivers with confidence scoring",
      "Full review intelligence — all languages, all platforms",
      "Bi-weekly market digest",
      "House Voice selector (3 voices)",
    ],
  },
  concierge: {
    name: "Concierge",
    icon: Crown,
    description: "AI runs your guest experience.",
    features: [
      "Everything in Insight",
      "AI literary guest welcomes — e-ink, TV, guidebook",
      "AI-researched place profiles with hours and tips",
      "Concierge picks with reasoning",
      "Weekly market digest with web citations",
      "All 5 House Voices including English Butler",
      "Priority support (4-hour response)",
    ],
  },
}

const PLAN_PRICES: Record<string, Record<string, number>> = {
  host:      { "1-3": 15, "4-5": 12, "6+": 10 },
  insight:   { "1-3": 25, "4-5": 20, "6+": 17 },
  concierge: { "1-3": 39, "4-5": 32, "6+": 27 },
}

const LOCKED_FEATURES_FOR_HOST = [
  "AI performance narrative and weekly actions",
  "Full review intelligence — all platforms",
  "AI ranked drivers with confidence scoring",
]

function getBracketLabel(min: number): string {
  if (min >= 6) return "6+"
  if (min >= 4) return "4-5"
  return "1-3"
}

function normalizeBillingPlan(plan: string): string {
  const p = (plan || "").toLowerCase().trim()
  if (p === "free") return "host"
  if (p === "pro") return "insight"
  if (p === "enterprise") return "concierge"
  if (p === "trialing" || p === "trial") return "concierge"
  return p || "host"
}

function BillingSettings({ billingParam, clearBillingParam }: { billingParam: string | null; clearBillingParam: () => void }) {
  const planContext = usePlanContext()
  const [loading, setLoading] = useState(true)
  const [billingData, setBillingData] = useState<{
    demo_tier_switch_enabled?: boolean
    stripe_customer_id: string | null
    property_count: number
    volume_bracket: { min: number; max: number; count: number }
    subscription: BillingSubscription | null
  } | null>(null)
  const [error, setError] = useState("")
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  const [toast, setToast] = useState<{ type: "success" | "info" | "error"; message: string } | null>(null)
  const [volumeExpanded, setVolumeExpanded] = useState(false)

  // Handle ?billing=success|canceled URL params
  useEffect(() => {
    if (billingParam === "success") {
      setToast({ type: "success", message: "Subscription activated! Your plan has been updated." })
      trackEvent("checkout_completed", { plan: currentPlan })
      clearBillingParam()
      if (planContext?.reload) planContext.reload()
    } else if (billingParam === "canceled") {
      setToast({ type: "info", message: "Checkout was canceled. No changes were made." })
      trackEvent("checkout_canceled", { plan: currentPlan })
      clearBillingParam()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // P1: billing_page_viewed
  useEffect(() => {
    if (!loading && billingData) {
      trackEvent("billing_page_viewed", { current_plan: currentPlan, is_trialing: isTrialing, trial_days_remaining: trialDaysLeft })
    }
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch billing status
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError("")
      try {
        const res = await apiClient.getBillingStatus()
        if (!cancelled) {
          if (res.ok && res.billing) {
            setBillingData(res.billing)
          } else {
            setError(res.error || "Failed to load billing information")
          }
        }
      } catch {
        if (!cancelled) setError("Failed to load billing information")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  // Account-level subscription (covers all properties).
  // Billing API is the source of truth for subscription state — prefer it
  // over planContext which goes through a property→owner resolution chain.
  const currentSub = billingData?.subscription || null
  const currentPlan = currentSub?.plan
    ? (currentSub.is_trialing ? "concierge" : normalizeBillingPlan(currentSub.plan))
    : planContext?.plan || "host"
  const isTrialing = currentSub?.is_trialing ?? planContext?.isTrialing ?? false
  const trialEndsAt = currentSub?.trial_ends_at || planContext?.trialEndsAt || null
  const bracketLabel = billingData ? getBracketLabel(billingData.volume_bracket.min) : "1-3"
  const isDemoTierSwitchEnabled = Boolean(billingData?.demo_tier_switch_enabled)

  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null

  async function handleCheckout(plan: string) {
    trackEvent("plan_card_clicked", { selected_plan: plan, current_plan: currentPlan })
    setCheckoutLoading(plan)
    try {
      trackEvent("checkout_started", { plan })
      const res = await apiClient.createCheckoutSession(plan)
      if (res.ok && res.checkout_url) {
        window.location.href = res.checkout_url
      } else {
        setToast({ type: "error", message: res.error || "Failed to create checkout session" })
        setCheckoutLoading(null)
      }
    } catch {
      setToast({ type: "error", message: "Failed to create checkout session" })
      setCheckoutLoading(null)
    }
  }

  async function handlePortal() {
    setPortalLoading(true)
    try {
      const res = await apiClient.createPortalSession()
      if (res.ok && res.portal_url) {
        window.location.href = res.portal_url
      } else {
        setToast({ type: "error", message: res.error || "Failed to open billing portal" })
        setPortalLoading(false)
      }
    } catch {
      setToast({ type: "error", message: "Failed to open billing portal" })
      setPortalLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-6 mt-4">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-5 w-64 mx-auto" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-6">
              <Skeleton className="h-6 w-20 rounded-full mb-3" />
              <Skeleton className="h-5 w-24 mb-2" />
              <Skeleton className="h-10 w-20 mb-1" />
              <Skeleton className="h-4 w-32 mb-4" />
              <div className="space-y-2 mb-6">
                {[0, 1, 2, 3].map((j) => (
                  <Skeleton key={j} className="h-4 w-full" />
                ))}
              </div>
              <Skeleton className="h-10 w-full rounded-lg" />
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card className="mt-4 border-destructive/50">
        <CardContent className="p-6">
          <p className="text-sm text-destructive">{error}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-6 mt-4">
      {/* Toast */}
      {toast && (
        <div className={cn(
          "px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-between",
          toast.type === "success" && "bg-success text-white",
          toast.type === "error" && "bg-destructive text-white",
          toast.type === "info" && "bg-muted text-muted-foreground",
        )}>
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-3 font-semibold hover:opacity-80"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Compact status bar */}
      <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {PLAN_INFO[currentPlan]?.name || currentPlan} Plan
          </span>
          {isTrialing && trialDaysLeft !== null ? (
            <Badge className={cn(
              "text-[11px]",
              trialDaysLeft <= 3
                ? "bg-warning text-warning-foreground"
                : "bg-primary text-primary-foreground"
            )}>
              Trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left
            </Badge>
          ) : currentSub?.status === "active" ? (
            <Badge className="bg-success text-white text-[11px]">Active</Badge>
          ) : currentSub?.status === "past_due" ? (
            <Badge className="bg-destructive text-destructive-foreground text-[11px]">Past Due</Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {currentSub?.cancel_at_period_end && (
            <span className="text-xs text-warning font-medium">Cancels at period end</span>
          )}
          {!isDemoTierSwitchEnabled && currentSub?.stripe_subscription_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePortal}
              disabled={portalLoading}
              className="text-xs"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              {portalLoading ? "Opening..." : "Manage Billing"}
            </Button>
          )}
        </div>
      </div>

      {/* Value prop line */}
      {isTrialing && currentPlan === "concierge" ? (
        <p className="text-center text-sm text-muted-foreground">
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-primary" />
          You have full Concierge access — subscribe now to keep it when your trial ends{trialDaysLeft !== null ? ` in ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}` : ""}
        </p>
      ) : currentPlan !== "concierge" ? (
        <p className="text-center text-sm text-muted-foreground">
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-primary" />
          Unlock AI insights that tell you what matters — upgrade in one click
        </p>
      ) : null}

      {isDemoTierSwitchEnabled && (
        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
          Demo account: tier changes apply instantly here without opening Stripe.
        </div>
      )}

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {(["host", "insight", "concierge"] as const).map((plan) => {
          const info = PLAN_INFO[plan]
          const Icon = info.icon
          const TIER_ORDER = { host: 0, insight: 1, concierge: 2 } as const
          const currentTier = TIER_ORDER[currentPlan as keyof typeof TIER_ORDER] ?? 0
          const planTier = TIER_ORDER[plan]
          const isCurrent = currentPlan === plan
          const isUpgrade = planTier > currentTier
          const isDowngrade = planTier < currentTier
          const isRecommended = plan === "concierge"
          const price = PLAN_PRICES[plan][bracketLabel]

          return (
            <Card
              key={plan}
              className={cn(
                "flex flex-col",
                isRecommended && !isCurrent && "border-primary shadow-md",
                isCurrent && "border-primary/50",
                !isRecommended && !isCurrent && "border-border",
              )}
            >
              <CardHeader className="pb-3">
                {/* Badge row */}
                <div className="flex items-center gap-2 min-h-[22px]">
                  {isRecommended && (
                    <Badge className="bg-teal-50 text-teal-700 border-0 text-[11px] font-semibold hover:bg-teal-50">
                      Recommended
                    </Badge>
                  )}
                  {isCurrent && isTrialing && (
                    <Badge variant="outline" className="text-[11px] font-semibold text-primary border-primary/30">
                      Trial active
                    </Badge>
                  )}
                  {isCurrent && !isTrialing && (
                    <Badge variant="outline" className="text-[11px] font-semibold text-muted-foreground border-border">
                      Current Plan
                    </Badge>
                  )}
                </div>

                {/* Plan name */}
                <div className="flex items-center gap-2 mt-1">
                  <Icon className="h-4 w-4 text-primary" />
                  <CardTitle className="text-[15px]">{info.name}</CardTitle>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-4xl font-bold text-foreground">${price}</span>
                </div>
                <span className="text-[13px] text-muted-foreground">per property / month</span>

                {/* Description */}
                <p className="text-sm text-muted-foreground mt-3 min-h-[40px]">
                  {info.description}
                </p>
              </CardHeader>

              <CardContent className="flex-1">
                {/* Feature list */}
                <ul className="flex flex-col gap-2">
                  {info.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 mt-0.5 text-primary flex-shrink-0" />
                      <span className="text-sm text-foreground leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* FOMO: Locked features on Host card */}
                {isCurrent && currentPlan === "host" && (
                  <>
                    <Separator className="my-3" />
                    <ul className="flex flex-col gap-2">
                      {LOCKED_FEATURES_FOR_HOST.map((f) => (
                        <li key={f} className="flex items-start gap-2 opacity-50">
                          <Lock className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-muted-foreground leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </CardContent>

              <CardFooter>
                {isCurrent && isTrialing ? (
                  <Button
                    className="w-full"
                    onClick={() => handleCheckout(plan)}
                    disabled={checkoutLoading !== null}
                  >
                    {checkoutLoading === plan ? (
                      isDemoTierSwitchEnabled ? "Switching..." : "Redirecting..."
                    ) : (
                      <>Subscribe — keep {info.name} after trial <ArrowRight className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                ) : isCurrent ? (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : isUpgrade ? (
                  <Button
                    className={cn(
                      "w-full",
                      isRecommended && "shadow-sm",
                    )}
                    onClick={() => handleCheckout(plan)}
                    disabled={checkoutLoading !== null}
                  >
                    {checkoutLoading === plan ? (
                      isDemoTierSwitchEnabled ? "Switching..." : "Redirecting..."
                    ) : isDemoTierSwitchEnabled ? (
                      <>Switch to {info.name} <ArrowRight className="h-4 w-4 ml-1" /></>
                    ) : !currentSub?.stripe_subscription_id ? (
                      <>Start 14-day trial <ArrowRight className="h-4 w-4 ml-1" /></>
                    ) : (
                      <>Upgrade to {info.name} <ArrowRight className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                ) : isDowngrade ? (
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={() => handleCheckout(plan)}
                    disabled={checkoutLoading !== null}
                  >
                    {checkoutLoading === plan
                      ? (isDemoTierSwitchEnabled ? "Switching..." : "Redirecting...")
                      : `Switch to ${info.name}`}
                  </Button>
                ) : null}
              </CardFooter>
            </Card>
          )
        })}
      </div>

      {/* Volume pricing — demoted to disclosure */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setVolumeExpanded(!volumeExpanded)}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown className={cn(
            "h-3 w-3 transition-transform",
            volumeExpanded && "rotate-180"
          )} />
          Volume pricing for 4+ properties
        </button>

        {volumeExpanded && (
          <Card className="mt-3 mx-auto max-w-md">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-3">
                You have {billingData?.property_count || 1} propert{(billingData?.property_count || 1) === 1 ? "y" : "ies"} — {bracketLabel} bracket.
                All prices per property per month. Billed monthly.
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 text-left font-medium text-muted-foreground">Properties</th>
                    <th className="py-2 text-center font-medium text-muted-foreground">Host</th>
                    <th className="py-2 text-center font-medium text-muted-foreground">Insight</th>
                    <th className="py-2 text-center font-medium text-muted-foreground">Concierge</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "1–3", bracket: "1-3" },
                    { label: "4–5", bracket: "4-5" },
                    { label: "6+", bracket: "6+" },
                  ].map(({ label, bracket }) => (
                    <tr key={bracket} className={cn(
                      "border-b border-border last:border-0",
                      bracket === bracketLabel && "bg-muted font-medium"
                    )}>
                      <td className="py-2">{label}</td>
                      <td className="py-2 text-center">${PLAN_PRICES.host[bracket]}</td>
                      <td className="py-2 text-center">${PLAN_PRICES.insight[bracket]}</td>
                      <td className="py-2 text-center">${PLAN_PRICES.concierge[bracket]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

// ============================================
// Privacy & Data Settings Tab
// ============================================

function PrivacySettings() {
  const { propertyId, property } = usePropertyContext()
  const { logout } = useAuth()
  const navigate = useNavigate()

  // Export state
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState("")
  const [exportSuccess, setExportSuccess] = useState("")

  // Anonymise state
  const [beforeDate, setBeforeDate] = useState("")
  const [showAnonConfirm, setShowAnonConfirm] = useState(false)
  const [anonConfirmText, setAnonConfirmText] = useState("")
  const [anonymising, setAnonymising] = useState(false)
  const [anonError, setAnonError] = useState("")
  const [anonResult, setAnonResult] = useState<{ bookings: number; bookings_normalized: number; calendar_events: number } | null>(null)

  // Account deletion state
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteAccountError, setDeleteAccountError] = useState("")

  async function handleExport() {
    setExporting(true)
    setExportError("")
    setExportSuccess("")
    try {
      const result = await apiClient.exportAccountData()
      if (result.ok && result.blob) {
        const url = URL.createObjectURL(result.blob)
        const a = document.createElement("a")
        a.href = url
        a.download = result.filename || "stayframe-export.json"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        setExportSuccess("Data exported successfully.")
      } else {
        setExportError(result.error || "Export failed.")
      }
    } catch {
      setExportError("Export failed. Please try again.")
    }
    setExporting(false)
  }

  async function handleAnonymise() {
    if (!propertyId || anonConfirmText !== "ANONYMISE GUEST DATA") return
    setAnonymising(true)
    setAnonError("")
    setAnonResult(null)
    try {
      const res = await apiClient.anonymiseGuests(propertyId, beforeDate, "ANONYMISE GUEST DATA")
      if (res.ok && res.anonymised) {
        setAnonResult(res.anonymised)
        setShowAnonConfirm(false)
        setAnonConfirmText("")
      } else {
        setAnonError(res.error || "Anonymisation failed.")
      }
    } catch {
      setAnonError("Anonymisation failed. Please try again.")
    }
    setAnonymising(false)
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== "DELETE MY ACCOUNT") return
    setDeletingAccount(true)
    setDeleteAccountError("")
    try {
      const res = await apiClient.deleteAccount("DELETE MY ACCOUNT")
      if (res.ok) {
        await logout()
        navigate("/login", { replace: true })
      } else {
        setDeleteAccountError(res.error || "Account deletion failed.")
        setDeletingAccount(false)
      }
    } catch {
      setDeleteAccountError("Account deletion failed. Please try again.")
      setDeletingAccount(false)
    }
  }

  return (
    <div>
      {/* Data Export */}
      <Card className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Download className="h-4 w-4 text-primary" />
            Export Your Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Download a JSON file containing all your account data, properties, bookings, reviews, and settings.
            Exports are limited to once per 24 hours.
          </p>
          {exportError && (
            <div className="login-card__error mb-3" role="alert">
              {exportError}
            </div>
          )}
          {exportSuccess && (
            <p className="text-sm text-green-600 mb-3">{exportSuccess}</p>
          )}
          <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export Data"}
          </Button>
        </CardContent>
      </Card>

      {/* Guest Anonymisation */}
      <Card className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-primary" />
            Anonymise Guest Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Replace guest names with "Guest" and remove country data for bookings checked out before a specified date.
            Revenue, dates, and party sizes are preserved for analytics. This action cannot be undone.
          </p>

          {anonResult && (
            <div className="rounded-md bg-muted p-3 mb-4">
              <p className="text-sm font-medium mb-1">Anonymisation complete</p>
              <p className="text-xs text-muted-foreground">
                {anonResult.bookings} booking{anonResult.bookings !== 1 ? "s" : ""},{" "}
                {anonResult.bookings_normalized} normalised record{anonResult.bookings_normalized !== 1 ? "s" : ""},{" "}
                {anonResult.calendar_events} calendar event{anonResult.calendar_events !== 1 ? "s" : ""} anonymised.
              </p>
            </div>
          )}

          {!showAnonConfirm ? (
            <div className="flex items-end gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Anonymise bookings checked out before
                </label>
                <input
                  type="date"
                  value={beforeDate}
                  onChange={(e) => setBeforeDate(e.target.value)}
                  className="login-card__input h-9 text-sm w-full md:w-[180px]"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!beforeDate) { setAnonError("Please select a date."); return }
                  setAnonError("")
                  setShowAnonConfirm(true)
                }}
                disabled={!beforeDate || !propertyId}
              >
                Anonymise
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-sm mb-3">
                This will anonymise guest data for {property?.name || "this property"} with check-out before <strong>{beforeDate}</strong>.
              </p>
              <p className="text-sm mb-2">
                Type <strong>ANONYMISE GUEST DATA</strong> to confirm:
              </p>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  value={anonConfirmText}
                  onChange={(e) => setAnonConfirmText(e.target.value)}
                  className="login-card__input h-9 text-sm flex-1 max-w-[280px]"
                  placeholder="ANONYMISE GUEST DATA"
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleAnonymise}
                  disabled={anonymising || anonConfirmText !== "ANONYMISE GUEST DATA"}
                >
                  {anonymising ? "Processing..." : "Confirm"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setShowAnonConfirm(false); setAnonConfirmText(""); setAnonError("") }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {anonError && (
            <div className="login-card__error mt-3" role="alert">
              {anonError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Account */}
      <Card className="bg-card/80 backdrop-blur-sm border border-destructive/30 shadow-sm mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Delete Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Permanently delete your account and all associated data including properties, bookings, reviews,
            and configuration. Active subscriptions will be cancelled. This action cannot be undone.
          </p>

          {!showDeleteAccount ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setShowDeleteAccount(true)}
            >
              Delete My Account
            </Button>
          ) : (
            <div>
              {deleteAccountError && (
                <div className="login-card__error mb-3" role="alert">
                  {deleteAccountError}
                </div>
              )}
              <p className="text-sm mb-2">
                Type <strong>DELETE MY ACCOUNT</strong> to confirm:
              </p>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="login-card__input h-9 text-sm flex-1 max-w-[280px]"
                  placeholder="DELETE MY ACCOUNT"
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || deleteConfirmText !== "DELETE MY ACCOUNT"}
                >
                  {deletingAccount ? "Deleting..." : "Confirm Delete"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(""); setDeleteAccountError("") }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Privacy Policy Link */}
      <div className="mt-6">
        <p className="text-xs text-muted-foreground">
          Read our{" "}
          <a href="/privacy" className="underline hover:text-foreground" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          {" "}for details on how we handle your data. For data protection inquiries, contact{" "}
          <a href="mailto:privacy@stayframe.ai" className="underline hover:text-foreground">privacy@stayframe.ai</a>.
        </p>
      </div>
    </div>
  )
}

// ============================================
// Danger Zone — Property Deletion
// ============================================

function DangerZone() {
  const { propertyId, property } = usePropertyContext()
  const { refreshSession } = useAuth()
  const navigate = useNavigate()
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState("")

  const propertyName = property?.name || ""

  async function handleDelete() {
    if (!propertyId || confirmText !== propertyName) return
    setDeleting(true)
    setDeleteError("")
    const res = await apiClient.deleteProperty(propertyId)
    if (res.ok) {
      await refreshSession()
      navigate("/properties", { replace: true })
    } else {
      setDeleteError(res.error || "Failed to delete property")
      setDeleting(false)
    }
  }

  return (
    <div className="section-card settings-danger-zone mt-8">
      <h2 className="settings-danger-zone__title">Danger Zone</h2>
      <p className="text-sm text-foreground/70 mb-4">
        Deleting this property will deactivate it and revoke all active sessions tied to it.
        This action can only be performed by the property owner.
      </p>

      {!showConfirm ? (
        <button
          type="button"
          className="sf-btn sf-btn--ghost sf-btn--sm text-destructive border-destructive"
          onClick={() => setShowConfirm(true)}
        >
          Delete this property
        </button>
      ) : (
        <div>
          {deleteError && (
            <div className="login-card__error mb-3" role="alert">
              {deleteError}
            </div>
          )}
          <p className="text-sm mb-3">
            Type <strong>{propertyName}</strong> to confirm:
          </p>
          <div className="flex gap-3 items-center">
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="login-card__input h-9 text-sm flex-1 max-w-[300px]"
              placeholder={propertyName}
              autoFocus
            />
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || confirmText !== propertyName}
              className={cn(
                "px-4 py-2 border-none rounded-sm text-sm font-medium h-9",
                confirmText === propertyName
                  ? "bg-destructive text-white cursor-pointer"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              )}
            >
              {deleting ? "Deleting..." : "Confirm Delete"}
            </button>
            <button
              type="button"
              onClick={() => { setShowConfirm(false); setConfirmText(""); setDeleteError("") }}
              className="data-table__action h-9"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// Alerts Settings Tab
// ============================================

function AlertsSettings() {
  const { propertyId } = usePropertyContext()
  const [subscriptions, setSubscriptions] = useState<AlertSubscription[]>([])
  const [events, setEvents] = useState<AlertEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formType, setFormType] = useState<"email" | "webhook">("email")
  const [formTarget, setFormTarget] = useState("")
  const [formSeverity, setFormSeverity] = useState("warning")
  const [formSaving, setFormSaving] = useState(false)
  const [formError, setFormError] = useState("")

  const fetchAlerts = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    const [subsRes, eventsRes] = await Promise.all([
      apiClient.getAlertSubscriptions(propertyId),
      apiClient.getAlertEvents(propertyId),
    ])
    if (subsRes.ok && subsRes.subscriptions) setSubscriptions(subsRes.subscriptions)
    if (eventsRes.ok && eventsRes.events) setEvents(eventsRes.events)
    setLoading(false)
  }, [propertyId])

  useEffect(() => { fetchAlerts() }, [fetchAlerts])

  async function handleAddSubscription() {
    if (!propertyId || !formTarget.trim()) return
    setFormSaving(true)
    setFormError("")
    const res = await apiClient.upsertAlertSubscription({
      type: formType,
      target: formTarget.trim(),
      enabled: true,
      severity_threshold: formSeverity,
    }, propertyId)
    if (res.ok) {
      setFormTarget("")
      setShowForm(false)
      fetchAlerts()
    } else {
      setFormError(res.error || "Failed to add subscription")
    }
    setFormSaving(false)
  }

  const [alertBusyId, setAlertBusyId] = useState<string | null>(null)

  async function handleToggle(sub: AlertSubscription) {
    if (!propertyId || alertBusyId) return
    setAlertBusyId(sub.id)
    await apiClient.upsertAlertSubscription({
      id: sub.id,
      type: sub.type,
      target: sub.target,
      enabled: !sub.enabled,
      severity_threshold: sub.severity_threshold,
    }, propertyId)
    setAlertBusyId(null)
    fetchAlerts()
  }

  async function handleDelete(subId: string) {
    if (!propertyId || alertBusyId) return
    setAlertBusyId(subId)
    await apiClient.deleteAlertSubscription(subId, propertyId)
    setAlertBusyId(null)
    fetchAlerts()
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="sf-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Subscriptions */}
      <div className="section-card">
        <div className="section-card__title flex items-center justify-between">
          <span>Alert Subscriptions</span>
          <button
            className="data-table__action"
            type="button"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancel" : "Add subscription"}
          </button>
        </div>

        {showForm && (
          <div className="settings-alert-form">
            {formError && <div className="login-card__error mb-3" role="alert">{formError}</div>}
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="login-card__label" htmlFor="alert-type">Type</label>
                <select
                  id="alert-type"
                  className="audit-filters__select w-full"
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as "email" | "webhook")}
                >
                  <option value="email">Email</option>
                  <option value="webhook">Webhook</option>
                </select>
              </div>
              <div>
                <label className="login-card__label" htmlFor="alert-target">
                  {formType === "email" ? "Email address" : "Webhook URL"}
                </label>
                <input
                  id="alert-target"
                  className="login-card__input h-[34px] text-sm"
                  type={formType === "email" ? "email" : "url"}
                  value={formTarget}
                  onChange={(e) => setFormTarget(e.target.value)}
                  placeholder={formType === "email" ? "alerts@example.com" : "https://hooks.example.com/..."}
                />
              </div>
              <div>
                <label className="login-card__label" htmlFor="alert-severity">Min severity</label>
                <select
                  id="alert-severity"
                  className="audit-filters__select w-full"
                  value={formSeverity}
                  onChange={(e) => setFormSeverity(e.target.value)}
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <button
                className="login-card__submit w-auto px-5 h-[34px]"
                onClick={handleAddSubscription}
                disabled={formSaving || !formTarget.trim()}
                type="button"
              >
                {formSaving ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        )}

        {subscriptions.length === 0 && !showForm ? (
          <div className="empty-state p-6">
            <div className="empty-state__title">No alert subscriptions</div>
            <div className="empty-state__desc">
              Add an email or webhook to receive alerts for job failures, token reuse, and other critical events.
            </div>
          </div>
        ) : subscriptions.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Target</th>
                <th>Min Severity</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td>
                    <span className={`audit-action-badge audit-action-badge--${sub.type === "email" ? "default" : "run"}`}>
                      {sub.type === "email" ? "Email" : "Webhook"}
                    </span>
                  </td>
                  <td className="font-mono text-xs truncate max-w-[300px]">
                    {sub.target}
                  </td>
                  <td>{severityLabel(sub.severity_threshold)}</td>
                  <td>
                    <button
                      className={`status-pill status-pill--${sub.enabled ? "healthy" : "neutral"} cursor-pointer border-none bg-transparent`}
                      type="button"
                      onClick={() => handleToggle(sub)}
                      disabled={alertBusyId === sub.id}
                      title={sub.enabled ? "Click to disable" : "Click to enable"}
                    >
                      <span className="status-pill__dot" />
                      {alertBusyId === sub.id ? "…" : sub.enabled ? "Active" : "Paused"}
                    </button>
                  </td>
                  <td>
                    <button
                      className="data-table__action text-destructive"
                      type="button"
                      onClick={() => handleDelete(sub.id)}
                      disabled={alertBusyId === sub.id}
                    >
                      {alertBusyId === sub.id ? "Removing…" : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : null}
      </div>

      {/* Recent Alert Events */}
      <div className="section-card">
        <h2 className="section-card__title">Recent Alert Events</h2>
        {events.length === 0 ? (
          <div className="empty-state p-6">
            <div className="empty-state__desc">No alert events yet</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Severity</th>
                <th>Type</th>
                <th>Summary</th>
                <th>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {events.map((evt) => (
                <tr key={evt.id}>
                  <td>
                    <span className="audit-row__time">{formatRelative(evt.ts)}</span>
                  </td>
                  <td>
                    <SeverityBadge severity={evt.severity} />
                  </td>
                  <td>{formatEventType(evt.event_type)}</td>
                  <td className="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {evt.summary}
                  </td>
                  <td>
                    <span className={`status-pill status-pill--${evt.delivery_status === "sent" ? "healthy" : evt.delivery_status === "failed" ? "error" : "neutral"}`}>
                      <span className="status-pill__dot" />
                      {evt.delivery_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  )
}

// ============================================
// Members Settings Tab
// ============================================

function MembersSettings() {
  const { propertyId } = usePropertyContext()
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formEmail, setFormEmail] = useState("")
  const [formRole, setFormRole] = useState("viewer")
  const [formSaving, setFormSaving] = useState(false)
  const [formError, setFormError] = useState("")
  const [error, setError] = useState("")

  const fetchMembers = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    setError("")
    const res = await apiClient.getMembers(propertyId)
    if (res.ok && res.members) {
      setMembers(res.members)
    } else {
      // Could be forbidden for non-owners
      const errObj = res as Record<string, unknown>
      if (errObj.code === "forbidden") {
        setError("Only property owners can manage members.")
      }
    }
    setLoading(false)
  }, [propertyId])

  useEffect(() => { fetchMembers() }, [fetchMembers])

  async function handleAddMember() {
    if (!propertyId || !formEmail.trim()) return
    setFormSaving(true)
    setFormError("")
    const res = await apiClient.addMember({ email: formEmail.trim(), role: formRole }, propertyId)
    if (res.ok) {
      setFormEmail("")
      setShowForm(false)
      fetchMembers()
    } else {
      setFormError(res.error || "Failed to add member")
    }
    setFormSaving(false)
  }

  async function handleRoleChange(userId: string, role: string) {
    if (!propertyId) return
    await apiClient.updateMemberRole(userId, role, propertyId)
    fetchMembers()
  }

  const [memberBusyId, setMemberBusyId] = useState<string | null>(null)

  async function handleRemove(userId: string) {
    if (!propertyId || memberBusyId) return
    setMemberBusyId(userId)
    await apiClient.removeMember(userId, propertyId)
    setMemberBusyId(null)
    fetchMembers()
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="sf-spinner" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="section-card">
        <div className="empty-state p-6">
          <div className="empty-state__title">Access Restricted</div>
          <div className="empty-state__desc">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="section-card">
      <div className="section-card__title flex items-center justify-between">
        <span>Property Members</span>
        <button
          className="data-table__action"
          type="button"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancel" : "Add member"}
        </button>
      </div>

      {showForm && (
        <div className="settings-alert-form">
          {formError && <div className="login-card__error mb-3" role="alert">{formError}</div>}
          <div className="grid grid-cols-[2fr_1fr] gap-3 items-end">
            <div>
              <label className="login-card__label" htmlFor="member-email">Email</label>
              <input
                id="member-email"
                className="login-card__input h-[34px] text-sm"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="login-card__label" htmlFor="member-role">Role</label>
              <select
                id="member-role"
                className="audit-filters__select w-full"
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <button
              className="login-card__submit w-auto px-5 h-[34px]"
              onClick={handleAddMember}
              disabled={formSaving || !formEmail.trim()}
              type="button"
            >
              {formSaving ? "Adding..." : "Add member"}
            </button>
          </div>
        </div>
      )}

      {members.length === 0 ? (
        <div className="empty-state p-6">
          <div className="empty-state__desc">No members found</div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Since</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id}>
                <td>
                  <div className="font-medium truncate">{m.name || m.email}</div>
                  {m.name && (
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  )}
                </td>
                <td>
                  {m.is_owner ? (
                    <span className="status-pill status-pill--healthy">
                      <span className="status-pill__dot" />
                      Owner
                    </span>
                  ) : (
                    <select
                      className="audit-filters__select min-w-[100px]"
                      value={m.role}
                      onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="owner">Owner</option>
                    </select>
                  )}
                </td>
                <td>
                  <span className="audit-row__time">{formatRelative(m.created_at)}</span>
                </td>
                <td>
                  {!m.is_owner && (
                    <button
                      className="data-table__action text-destructive"
                      type="button"
                      onClick={() => handleRemove(m.user_id)}
                      disabled={memberBusyId === m.user_id}
                    >
                      {memberBusyId === m.user_id ? "Removing…" : "Remove"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ============================================
// Shared Components
// ============================================

function SettingsField({
  label,
  value,
  onChange,
  type = "text",
  hint,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: "text" | "password" | "select"
  hint?: string
  options?: Array<{ value: string; label: string }>
}) {
  const id = `settings-${label.toLowerCase().replace(/\s+/g, "-")}`

  return (
    <div>
      <label className="login-card__label" htmlFor={id}>
        {label}
      </label>
      {type === "select" && options ? (
        <select
          id={id}
          className="sf-field-select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className="login-card__input h-9 text-sm"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {hint && (
        <div className="settings-field-hint">{hint}</div>
      )}
    </div>
  )
}

// ============================================
// Address Autocomplete (Google Places)
// ============================================

const TIMEZONE_OPTIONS = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf("timeZone")
  } catch {
    return [
      "Europe/Stockholm", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Amsterdam",
      "Europe/Rome", "Europe/Madrid", "Europe/Zurich", "Europe/Oslo", "Europe/Copenhagen",
      "Europe/Helsinki", "Europe/Athens", "Europe/Istanbul", "Europe/Moscow",
      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "America/Toronto", "America/Vancouver", "America/Sao_Paulo", "America/Mexico_City",
      "Asia/Tokyo", "Asia/Shanghai", "Asia/Singapore", "Asia/Dubai", "Asia/Kolkata",
      "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
      "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos",
    ]
  }
})()

function TimezoneSelect({ value, onChange }: { value: string; onChange: (tz: string) => void }) {
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const filtered = TIMEZONE_OPTIONS.filter(tz =>
    tz.toLowerCase().includes(search.toLowerCase())
  )
  const commitTimezone = useCallback((raw: string): boolean => {
    const normalized = raw.trim().toLowerCase()
    if (!normalized) return false
    const match = TIMEZONE_OPTIONS.find((tz) => tz.toLowerCase() === normalized)
    if (!match) return false
    onChange(match)
    setSearch(match)
    return true
  }, [onChange])

  return (
    <div className="relative">
      <label className="login-card__label">Timezone</label>
      <input
        className="login-card__input h-9 text-sm"
        type="text"
        value={open ? search : value || ""}
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => { setSearch(value || ""); setOpen(true) }}
        onBlur={() => setTimeout(() => { commitTimezone(search); setOpen(false) }, 200)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            if (!commitTimezone(search) && filtered.length > 0) {
              onChange(filtered[0])
              setSearch(filtered[0])
            }
            setOpen(false)
          }
        }}
        placeholder="Search timezone..."
      />
      {open && filtered.length > 0 && (
        <div className="sf-autocomplete-dropdown">
          {filtered.map(tz => (
            <div
              key={tz}
              onMouseDown={() => { onChange(tz); setOpen(false) }}
              className={`sf-autocomplete-dropdown__item${tz === value ? " sf-autocomplete-dropdown__item--active" : ""}`}
            >
              {tz}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
}: {
  value: string
  onChange: (address: string) => void
  onPlaceSelect: (details: { address: string; lat: number | null; lng: number | null; utc_offset: number | null; timezone: string | null }) => void
}) {
  const [search, setSearch] = useState(value)
  const [predictions, setPredictions] = useState<Array<{ place_id: string; description: string }>>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fallback, setFallback] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => { if (!open) setSearch(value) }, [value, open])

  function handleInput(q: string) {
    setSearch(q)
    onChange(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (q.length < 3 || fallback) { setPredictions([]); setOpen(false); return }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await apiClient.googlePlacesAutocomplete(q)
        if (res.ok && res.predictions) {
          setPredictions(res.predictions)
          setOpen(res.predictions.length > 0)
        } else if (!res.ok && res.error?.includes("not configured")) {
          setFallback(true)
        }
      } catch {
        // Silently fall back to plain text
      }
      setLoading(false)
    }, 300)
  }

  async function handleSelect(placeId: string, description: string) {
    setOpen(false)
    setSearch(description)
    setPredictions([])
    try {
      const details = await apiClient.googlePlacesDetails(placeId)
      if (details.ok) {
        onPlaceSelect({
          address: details.address || description,
          lat: details.lat ?? null,
          lng: details.lng ?? null,
          utc_offset: details.utc_offset ?? null,
          timezone: details.timezone ?? null,
        })
      }
    } catch {
      onChange(description)
    }
  }

  return (
    <div className="relative">
      <label className="login-card__label">Address</label>
      <input
        className="login-card__input h-9 text-sm"
        type="text"
        value={search}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Start typing an address..."
      />
      {loading && <div className="sf-spinner sf-spinner--xs sf-autocomplete-spinner" />}
      {open && predictions.length > 0 && (
        <div className="sf-autocomplete-dropdown">
          {predictions.map(p => (
            <div
              key={p.place_id}
              onMouseDown={() => handleSelect(p.place_id, p.description)}
              className="sf-autocomplete-dropdown__item"
            >
              {p.description}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SeverityBadge({ severity }: { severity: string }) {
  const variant = severity === "critical" ? "error"
    : severity === "warning" ? "warning"
    : "neutral"
  return (
    <span className={`status-pill status-pill--${variant}`}>
      <span className="status-pill__dot" />
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  )
}

// ============================================
// Helpers
// ============================================

function formatClock(isoString: string | null | undefined, timezone?: string): string {
  if (!isoString) return ""
  try {
    const d = new Date(isoString)
    return d.toLocaleTimeString("sv-SE", {
      timeZone: timezone || undefined,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
  } catch {
    return ""
  }
}

function severityLabel(s: string): string {
  const map: Record<string, string> = { info: "Info+", warning: "Warning+", critical: "Critical only" }
  return map[s] || s
}

function formatEventType(type: string): string {
  const labels: Record<string, string> = {
    refresh_token_reuse: "Token Reuse",
    calendar_sync_failures: "Calendar Failures",
    research_run_failures: "Research Failures",
    rate_limit_sustained: "Rate Limit",
  }
  return labels[type] || type.replace(/_/g, " ")
}

function formatRelative(iso: string): string {
  if (!iso) return "\u2014"
  try {
    const d = new Date(iso)
    const diffMs = Date.now() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMs / 3600000)
    const diffDay = Math.floor(diffMs / 86400000)
    if (diffMin < 1) return "Just now"
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 7) return `${diffDay}d ago`
    return d.toLocaleDateString()
  } catch {
    return iso
  }
}
